/**
 * Cookie Consent Management Utility
 * Handles user cookie preferences and Google Consent Mode v2 integration
 */

import { getRootDomain } from './root-domain';

export type ConsentState = {
  functional: boolean;
  timestamp: number;
  version: number;
};

const CONSENT_COOKIE_NAME = 'cookie_consent';
const CONSENT_VERSION = 2; // Incremented to reset all existing cookies
const SHOW_COUNT_KEY = 'cookie_banner_show_count';
const SESSION_COUNTED_KEY = 'cookie_banner_counted_this_session';
const MAX_SHOWS = 2; // Lifetime cap — once shown 2 sessions, never show again

/**
 * Get current consent state from cookies
 */
export function getConsentState(): ConsentState | null {
  if (typeof window === 'undefined') return null;

  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

  if (!cookieValue) return null;

  try {
    const value = cookieValue.split('=')[1];
    return JSON.parse(decodeURIComponent(value));
  } catch {
    return null;
  }
}

/**
 * Save consent state to cookies
 */
export function saveConsentState(functional: boolean): void {
  if (typeof window === 'undefined') return;

  const state: ConsentState = {
    functional,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };

  // Set cookie for 1 year. Dual-write pattern: host-only for back-compat
  // with every existing user, plus a domain-scoped copy so rate.goldcenter.am
  // sees the same consent state and doesn't re-prompt after the user already
  // dismissed the banner on goldcenter.am.
  const maxAge = 365 * 24 * 60 * 60; // 1 year in seconds
  const encoded = encodeURIComponent(JSON.stringify(state));
  document.cookie = `${CONSENT_COOKIE_NAME}=${encoded}; path=/; max-age=${maxAge}; SameSite=Lax`;
  const rootDomain = getRootDomain();
  if (rootDomain) {
    document.cookie = `${CONSENT_COOKIE_NAME}=${encoded}; path=/; max-age=${maxAge}; SameSite=Lax; domain=${rootDomain}`;
  }

  // Analytics is always granted (mandatory)
  updateGoogleConsent(true);
}

/**
 * Update Google Consent Mode based on user preference
 */
export function updateGoogleConsent(analyticsAccepted: boolean): void {
  if (typeof window === 'undefined' || !(window as any).gtag) return;

  const gtag = (window as any).gtag;
  gtag('consent', 'update', {
    analytics_storage: analyticsAccepted ? 'granted' : 'denied',
  });
}

/**
 * Check if banner should be shown.
 * Returns false if the user already made a choice OR if the banner has
 * already been shown 2 sessions (lifetime cap — counter never resets).
 */
export function shouldShowBanner(): boolean {
  if (typeof window === 'undefined') return false;

  const state = getConsentState();
  if (state) return false;

  return getShowCount() < MAX_SHOWS;
}

/**
 * Read the lifetime show count from localStorage.
 */
export function getShowCount(): number {
  if (typeof window === 'undefined') return 0;
  const raw = localStorage.getItem(SHOW_COUNT_KEY);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Mark the banner as shown for this session — increments the lifetime count
 * at most once per browser session regardless of how many pages the user
 * views. The count never resets, even on consent version bumps.
 */
export function markBannerShownThisSession(): void {
  if (typeof window === 'undefined') return;
  if (sessionStorage.getItem(SESSION_COUNTED_KEY)) return;

  const next = getShowCount() + 1;
  localStorage.setItem(SHOW_COUNT_KEY, String(next));
  sessionStorage.setItem(SESSION_COUNTED_KEY, '1');
}

/**
 * Accept all cookies (functional cookies)
 */
export function acceptAllCookies(): void {
  saveConsentState(true);
}

/**
 * Reject functional cookies (only necessary cookies + analytics)
 */
export function rejectAllCookies(): void {
  saveConsentState(false);
}

/**
 * Check if functional cookies are accepted
 */
export function isFunctionalAccepted(): boolean {
  const state = getConsentState();
  return state?.functional ?? false;
}

/**
 * Analytics cookies are always accepted (mandatory)
 */
export function isAnalyticsAccepted(): boolean {
  return true; // Always true - analytics is mandatory
}

/**
 * Initialize Google Consent Mode with analytics always granted
 * Analytics is now mandatory (strictly necessary)
 */
export function initializeGoogleConsent(): void {
  if (typeof window === 'undefined') return;

  // Define dataLayer and gtag function if not already defined
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;

  // Set default consent to granted for analytics (mandatory)
  gtag('consent', 'default', {
    analytics_storage: 'granted',
  });
}